/**
 * RGM Studio — Project Details (opened from My Studio Projects).
 * Edit song title, cover, description, genre and tags; "Open Studio"
 * enters the recorder. Recordings/drafts are never touched here.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import SharedNavigation from "../components/SharedNavigation";
import { TagInput } from "../components/studio/TagInput";
import { StudioVersionsPanel } from "../components/studio/StudioVersionsPanel";
import { FinalMixPanel } from "../components/studio/FinalMixPanel";
import { SUBMIT_MUSIC_GENRES } from "../lib/musicGenres";

export const GENRES = SUBMIT_MUSIC_GENRES;
const MAX_COVER_BYTES = 8 * 1024 * 1024;

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">{label}</span>
    <div className="mt-1.5">{children}</div>
  </label>
);
const inputCls = "w-full h-11 px-3 rounded-xl bg-black/40 border border-white/10 text-white placeholder:text-gray-500 focus:outline-none focus:border-fuchsia-400/60";

export default function StudioProjectDetailsPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!form || window.location.hash !== "#final-mix") return;
    setTimeout(() => document.getElementById("final-mix")?.scrollIntoView({ behavior: "smooth" }), 300);
  }, [!!form]); // eslint-disable-line

  useEffect(() => {
    api.get(`/studio/projects/${projectId}`)
      .then((r) => {
        setData(r.data);
        const p = r.data.project;
        setForm({ title: p.title || "", description: p.description || "", genre: p.genre || "",
          tags: p.tags || [], cover_url: p.cover_url || null });
      })
      .catch(() => { toast.error("Could not open this project"); navigate("/studio"); });
  }, [projectId, navigate]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const onCover = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) { toast.error("Use a JPG, PNG or WEBP image"); return; }
    if (file.size > MAX_COVER_BYTES) { toast.error("Cover must be 8 MB or smaller"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const r = await api.post("/posts/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("cover_url")(r.data.image_url || r.data.url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Cover upload failed");
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Song title is required"); return; }
    setSaving(true);
    try {
      const r = await api.patch(`/studio/projects/${projectId}`, form);
      setData((d) => ({ ...d, project: r.data.project }));
      toast.success("Project saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save project");
    } finally { setSaving(false); }
  };

  if (!form) return <div className="min-h-screen bg-[#08080c]"><SharedNavigation activePage="studio" /></div>;
  const p = data.project;
  const takeCount = (data.takes || []).length;
  const cover = form.cover_url || p.beat_ref?.cover_url;
  const dirty = form.title !== (p.title || "") || form.description !== (p.description || "") || form.genre !== (p.genre || "")
    || JSON.stringify(form.tags) !== JSON.stringify(p.tags || []) || (form.cover_url || null) !== (p.cover_url || null);

  return (
    <div className="min-h-screen bg-[#08080c] text-white" data-testid="studio-project-details">
      <SharedNavigation activePage="studio" />
      <div className="max-w-2xl mx-auto px-4 pb-28" style={{ paddingTop: "calc(6rem + var(--rgm-banner-h, 0px))" }}>
        <Link to="/studio" className="text-xs text-gray-400 hover:text-white" data-testid="project-back-link">← My Studio Projects</Link>

        <div className="mt-4 flex gap-4 items-start">
          <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
            className="relative w-28 h-28 sm:w-36 sm:h-36 shrink-0 rounded-2xl overflow-hidden border border-white/10 bg-gradient-to-br from-fuchsia-700/40 to-purple-950"
            data-testid="project-cover-btn">
            {cover ? <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover" data-testid="project-cover-img" />
              : <span className="absolute inset-0 flex items-center justify-center text-[11px] text-gray-300 font-bold">Add cover</span>}
            <span className="absolute bottom-0 inset-x-0 bg-black/60 text-[10px] py-1 font-bold">{uploading ? "Uploading…" : "Change cover"}</span>
          </button>
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onCover} data-testid="project-cover-input" />
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-widest text-amber-300/80 font-bold">Song project</p>
            <h1 className="text-2xl sm:text-3xl font-black break-words" data-testid="project-title-display">{p.title || "Untitled Session"}</h1>
            <p className="text-xs text-gray-400 mt-1" data-testid="project-meta">
              {p.beat_source ? `Beat: ${p.beat_ref?.title || "Uploaded beat"}` : "No beat yet"} · {takeCount} {takeCount === 1 ? "take" : "takes"}
            </p>
            <button type="button" onClick={() => navigate(`/studio/session/${projectId}`)}
              className="mt-3 h-11 px-5 rounded-full bg-gradient-to-b from-fuchsia-500 to-purple-700 ring-1 ring-amber-300/40 font-black text-sm"
              data-testid="project-open-studio-btn">
              Open Studio
            </button>
          </div>
        </div>

        <div className="mt-8 space-y-4">
          <Field label="Song title">
            <input value={form.title} maxLength={80} onChange={(e) => set("title")(e.target.value)} className={inputCls} data-testid="project-title-input" />
          </Field>
          <Field label="Description">
            <textarea value={form.description} maxLength={1000} rows={4} onChange={(e) => set("description")(e.target.value)}
              placeholder="What's this song about?"
              className="w-full px-3 py-2 rounded-xl bg-black/40 border border-white/10 text-white placeholder:text-gray-500 focus:outline-none focus:border-fuchsia-400/60"
              data-testid="project-description-input" />
          </Field>
          <Field label="Genre">
            <select value={form.genre} onChange={(e) => set("genre")(e.target.value)} className={inputCls} style={{ colorScheme: "dark" }} data-testid="project-genre-select">
              <option value="" className="bg-[#1a1a2e] text-white">Choose a genre</option>
              {[...GENRES, ...(form.genre && !GENRES.includes(form.genre) ? [form.genre] : [])].map((g) => (
                <option key={g} value={g} className="bg-[#1a1a2e] text-white">{g}</option>
              ))}
            </select>
          </Field>
          <Field label="Tags">
            <TagInput value={form.tags} onChange={set("tags")} />
          </Field>
          <button type="button" onClick={save} disabled={saving || uploading}
            className="w-full h-12 rounded-xl bg-amber-400 hover:bg-amber-300 text-black font-black disabled:opacity-50"
            data-testid="project-save-btn">
            {saving ? "Saving…" : "Save details"}
          </button>
        </div>

        <FinalMixPanel projectId={projectId} dirty={dirty} />

        <StudioVersionsPanel key={projectId} projectId={projectId} onRestored={(pid) => navigate(`/studio/project/${pid}`)} />
      </div>
    </div>
  );
}
